// AddPerkataan.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useTheme } from "@emotion/react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Button, Snackbar, Alert } from "@mui/material";

import HeaderLayout from "../../../../components/HeaderLayout";
import InputTextField from "../../../../components/InputTextField";
import SearchableDropdown from "../../../../components/SearchableDropdown";
import FileUpload from "../../../../components/FileUpload";
import FormikSubmitButton from "../../../../components/FormikSubmitButton";
import TextArea from "../../../../components/TextArea";
import api from "../../../../api/apiClient";
import { handleUpload } from "../../../../utils/uploadFile";

const kategoriBudayaOptions = [
  "Agama",
  "Mental & Emosi",
  "Material",
  "Sosial",
  "Ekologi",
  "Politik",
  "Linguistik",
  "Sastera",
];

const validationSchema = Yup.object()
  .shape({
    kategoribudaya: Yup.string().required("Kategori Budaya is required"),
    perkataan: Yup.string().required("Perkataan is required"),
    ayatalquran: Yup.mixed().required("Ayat Al-Quran is required"),
    denotasi: Yup.string().required("Denotasi is required"),
    konotasi: Yup.string().required("Konotasi is required"),
    terjemahanayat: Yup.string().nullable(),
    terjemahanImage: Yup.mixed().nullable(),
    tafsiranUlama1: Yup.string().required("Tafsiran Ulama 1 is required"),
    tafsiranUlama2: Yup.string().required("Tafsiran Ulama 2 is required"),
  })
  .test(
    "terjemahan-required",
    "Either Terjemahan Ayat text or image is required",
    function (values) {
      const { terjemahanayat, terjemahanImage } = values || {};

      // Pass validation if either one has a value
      const hasText = !!terjemahanayat?.trim();
      const hasImage =
        terjemahanImage && terjemahanImage !== null && terjemahanImage !== "";

      return hasText || hasImage;
    }
  );

export default function AddPerkataan() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const navigateBack = () => navigate(-1);

  const handleSubmit = async (
    values,
    { setSubmitting, setFieldError, resetForm }
  ) => {
    try {
      let videoUrl = null;
      let imageUrl = null;

      if (values.ayatalquran) {
        videoUrl = await handleUpload(values.ayatalquran, "ayat-videos");
      }

      if (values.terjemahanImage) {
        imageUrl = await handleUpload(
          values.terjemahanImage,
          "terjemahan-assets"
        );
      }

      const payload = {
        KategoriBudaya: values.kategoribudaya,
        Perkataan: values.perkataan,
        Denotasi: values.denotasi?.trim(),
        Konotasi: values.konotasi?.trim(),
        TafsiranUlama1: values.tafsiranUlama1?.trim(),
        TafsiranUlama2: values.tafsiranUlama2?.trim(),
      };

      if (videoUrl) payload.AyatAlQuran = videoUrl;
      if (values.terjemahanayat?.trim())
        payload.TerjemahanAyat = values.terjemahanayat.trim();
      if (imageUrl) payload.TerjemahanImage = imageUrl;

      const res = await api.post("/perkataan", payload);

      if (!res.data.success) {
        throw new Error(
          res.data.message || "Server returned unsuccessful response"
        );
      }

      setSnackbar({
        open: true,
        message: "Successfully created new perkataan!",
        severity: "success",
      });

      setTimeout(() => {
        navigate("/");
      }, 800);
    } catch (error) {
      console.error("Error adding perkataan:", error.message);
      setFieldError("submit", error.message);

      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to create perkataan",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = {
    kategoribudaya: "",
    perkataan: "",
    ayatalquran: null,
    terjemahanayat: "",
    terjemahanImage: null,
    denotasi: "",
    konotasi: "",
    tafsiranUlama1: "",
    tafsiranUlama2: "",
  };

  return (
    <div className="flex flex-col h-screen mr-1 ml-1">
      <div className="flex-shrink-0">
        <HeaderLayout name="Add Perkataan" />
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        {({ isSubmitting, errors, values }) => {
          const canSubmit =
            values.kategoribudaya?.trim() &&
            values.perkataan?.trim() &&
            values.ayatalquran &&
            values.denotasi?.trim() &&
            values.konotasi?.trim() &&
            values.tafsiranUlama1?.trim() &&
            values.tafsiranUlama2?.trim() &&
            (values.terjemahanayat?.trim() || values.terjemahanImage);

          return (
            <Form>
              <div className="overflow-y-auto h-[62vh] px-5 py-6 space-y-10 hide-scrollbar">
                {errors.submit && (
                  <div className="bg-red-100 text-red-700 p-3 rounded-md">
                    {errors.submit}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-12">
                  <SearchableDropdown
                    label="Kategori Budaya"
                    name="kategoribudaya"
                    requiredMark
                    options={kategoriBudayaOptions}
                    placeholder="Select Kategori Budaya"
                  />
                  <InputTextField
                    label="Perkataan"
                    name="perkataan"
                    requiredMark
                    placeholder="Enter Perkataan"
                    type="text"
                  />
                </div>

                <div className="grid grid-cols-2 gap-8">
                  {/* Left column: Video Upload */}
                  <div className="space-y-4">
                    <FileUpload
                      label="Ayat Al-Quran"
                      placeholder="Upload or drag MP4 file"
                      accept="video/mp4"
                      name="ayatalquran"
                      requiredMark
                    />
                  </div>

                  {/* Right column: Translation + Image */}
                  <div className="space-y-4">
                    <TextArea
                      label="Terjemahan Ayat"
                      placeholder="Write terjemahan ayat"
                      name="terjemahanayat"
                      requiredMark
                    />

                    <FileUpload
                      label="Terjemahan Ayat (Gambar)"
                      placeholder="Upload or drag translated verse image"
                      accept="image/*"
                      name="terjemahanImage"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-12">
                  <TextArea
                    label="Makna Denotasi"
                    placeholder="Write Makna Denotasi"
                    name="denotasi"
                  />
                  <TextArea
                    label="Makna Konotasi"
                    placeholder="Write Makna Konotasi"
                    name="konotasi"
                  />
                </div>
                <div className="grid grid-cols-2 gap-12">
                  <TextArea
                    label="Tafsiran Ulama 1 (Tafsir Al-Tahrir wa Al-Tanwir)"
                    placeholder="Write Tafsiran Ulama 1"
                    name="tafsiranUlama1"
                  />
                  <TextArea
                    label="Tafsiran Ulama 2 (Tafsir Al-Munir)"
                    placeholder="Write Tafsiran Ulama 2"
                    name="tafsiranUlama2"
                  />
                </div>
              </div>

              <div className="flex-shrink-0 flex items-center gap-4 justify-center pt-4 border-t bg-white">
                <Button
                  onClick={navigateBack}
                  className="w-[90px] h-[40px]"
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.buttonHover.main,
                    "&:hover": {
                      backgroundColor: theme.palette.buttonHover.secondary,
                    },
                    color: theme.palette.white.main,
                    textTransform: "none",
                  }}
                >
                  Back
                </Button>
                <FormikSubmitButton
                  name="Submit"
                  canSubmit={canSubmit} // Pass canSubmit prop
                  className="w-[90px] h-[40px]"
                />
              </div>
            </Form>
          );
        }}
      </Formik>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
